/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { RouteId, RouteParam } from '../../../../router/routes';
import { ListTableColumnConfig } from '../../Common/ListTypes';
import {
    DocType,
    DocumentsListFilterParams,
    DocumentsListItemDto,
} from '../../../../data/api/documents/documentsClient.typings';
import {
    checkStringValueAndTranslate,
    checkValue,
    DATA_UNAVAILABLE,
    formatStringUtcDateTime,
    filterConfigToOmitKeys,
} from '../../../../utils/utils';
import { WithTranslation } from 'react-i18next';
import { NameValueDataItem } from '../../Common/NameValueGroup';
import { theme } from '../../../../assets/style/them';
import { paymentTypesAsIcons } from './DocumentCardViewItem';
import { FilterChipProps } from '../../../FilterPanel/SelectedFiletrsPanel';
import { TimeRangeFilterParams, TimeRangeFilterType } from '../../Common/TimeRangeFilter';
import { makeChipTextForRangeFilter } from '../../../FilterPanel/utils';
import { DOC_TYPE_L10N } from '../../../../data/translation/sharedLocales';
import { formatDocumentSum, makeDocumentTitle } from '../utils';
import { AppAbility } from '../../../../accessibility/ability';

export interface DocumentsListTableData {
    docTypeId: DocType;
    title: string;
    restored: boolean;
    documentId: string;
    documentIdHash: string;
    createdAt: string;
    companyTaxNumber: string;
    companyName: string;
    storeTaxNumber: string;
    cashBoxTaxNumber: string;
    sum?: string;
    taxSum?: string;
    taxRates?: string;
    paymentTypes?: string[] | typeof DATA_UNAVAILABLE;
    parentDocumentId?: string;
    period?: string;
    numberOfDocs?: string;
    globalDocumentNumber?: number;
    docNumber?: number;
    documentNumberInShift?: number;
    rrn?: string;
}

export const createTableData = (
    documents: DocumentsListItemDto[],
    t: WithTranslation['t'],
): DocumentsListTableData[] => {
    return documents.reduce((tableData, document) => {
        const dataItem: Partial<DocumentsListTableData> = {};

        dataItem.docTypeId = document.docTypeId;
        dataItem.title = makeDocumentTitle(document.docTypeId, document.docNumber, t);
        dataItem.restored = document.restored === true; //null or undefined means false

        dataItem.documentId = checkValue(document.documentId);
        dataItem.documentIdHash = checkValue(document.documentIdHash);
        dataItem.companyTaxNumber = checkValue(document.companyTaxNumber);
        dataItem.companyName = checkValue(document.companyName);
        dataItem.storeTaxNumber = checkValue(document.storeTaxNumber);
        dataItem.cashBoxTaxNumber = checkValue(document.cashBoxTaxNumber);
        if (document.parentDocumentId) dataItem.parentDocumentId = checkValue(document.parentDocumentId);

        dataItem.createdAt = checkValue(formatStringUtcDateTime(document.createdAtUtc));
        if (document.docTypeId !== DocType.OpenShift && document.docTypeId !== DocType.ZReport) {
            if (document.taxRates && document.taxRates.length > 0) {
                dataItem.taxRates = document.taxRates
                    .map((item) => {
                        return t('document.taxRates.' + item);
                    })
                    .join('  ');
            } else dataItem.taxRates = DATA_UNAVAILABLE;

            if (document.paymentTypes && document.paymentTypes.length > 0)
                dataItem.paymentTypes = document.paymentTypes;
            else dataItem.paymentTypes = DATA_UNAVAILABLE;
        }

        dataItem.sum = formatDocumentSum(document.docTypeId, document.sum);

        if (document.docTypeId !== DocType.OpenShift)
            dataItem.taxSum = formatDocumentSum(document.docTypeId, document.taxSum);

        if (document.docTypeId === DocType.ZReport) dataItem.numberOfDocs = checkValue(document.numberOfDocs);

        if (document.docTypeId === DocType.Correction || document.docTypeId === DocType.ZReport) {
            const periodFrom = checkValue(formatStringUtcDateTime(document.periodFrom));
            const periodTo = checkValue(formatStringUtcDateTime(document.periodTo));
            dataItem.period = `${periodFrom} - ${periodTo}`;
        }

        tableData.push(dataItem);

        return tableData;
    }, []);
};

export const createSelectedFiltersChips = (
    filterParams: DocumentsListFilterParams,
    timeRangeFilterParams: TimeRangeFilterParams,
    t: WithTranslation['t'],
): FilterChipProps<DocumentsListFilterParams>[] => {
    let chips: FilterChipProps<DocumentsListFilterParams>[] = [];

    if (filterParams.search_text && filterParams.search_text.length > 0) {
        chips.push({
            keyForValue: 'search_text',
            formattedValue: filterParams.search_text,
            resetValue: '',
        });
    }

    if (
        checkIsDocNumbersFilterShown(filterParams) &&
        filterParams.docNumbersSearchText &&
        filterParams.docNumbersSearchText.length > 0
    ) {
        chips.push({
            keyForValue: 'docNumbersSearchText',
            formattedValue: filterParams.docNumbersSearchText,
            resetValue: '',
        });
    }

    if (!checkIsTimeRangeFilterHidden(filterParams)) {
        if (timeRangeFilterParams) {
            switch (timeRangeFilterParams.shownFilter) {
                case TimeRangeFilterType.COMMONLY_USED:
                    chips.push({
                        keyForValue: 'dateFrom',
                        pairedKeyForValue: 'dateTo',
                        formattedValue: `${t('RegistryScreens.timeRange')}: ${checkStringValueAndTranslate(
                            timeRangeFilterParams.commonlyUsed,
                            'app.periodsCommonlyUsed.',
                            t,
                        )}`,
                        fitContent: true,
                        nonResettable: true,
                    });
                    break;
                case TimeRangeFilterType.QUICK_SELECT:
                    chips.push({
                        keyForValue: 'dateFrom',
                        pairedKeyForValue: 'dateTo',
                        formattedValue: `${t('RegistryScreens.timeRange')}: ${t('app.last')} ${
                            timeRangeFilterParams.quickSelect.value
                        } ${checkStringValueAndTranslate(
                            timeRangeFilterParams.quickSelect.units,
                            'app.timeUnits.',
                            t,
                        )}`,
                        fitContent: true,
                        nonResettable: true,
                    });
                    break;
                case TimeRangeFilterType.SHOW_DATES:
                    const fromDate = timeRangeFilterParams.showDates.dateFrom
                        ? formatStringUtcDateTime(timeRangeFilterParams.showDates.dateFrom)
                        : timeRangeFilterParams.showDates.dateFrom;
                    const toDate = timeRangeFilterParams.showDates.dateTo
                        ? formatStringUtcDateTime(timeRangeFilterParams.showDates.dateTo)
                        : timeRangeFilterParams.showDates.dateTo;
                    chips.push({
                        keyForValue: 'dateFrom',
                        pairedKeyForValue: 'dateTo',
                        formattedValue: makeChipTextForRangeFilter(t('RegistryScreens.timeRange'), fromDate, toDate),
                        fitContent: true,
                        nonResettable: true,
                    });
                    break;
            }
        }
    }

    if (filterParams.docTypeIds && filterParams.docTypeIds.length > 0) {
        const items: FilterChipProps<DocumentsListFilterParams>[] = filterParams.docTypeIds.map((item, index) => {
            return {
                keyForValue: 'docTypeIds',
                index,
                formattedValue: t(`${DOC_TYPE_L10N[item]}`),
            };
        });
        chips = [...chips, ...items];
    }

    if (filterParams.paymentTypes && filterParams.paymentTypes.length > 0) {
        const items: FilterChipProps<DocumentsListFilterParams>[] = filterParams.paymentTypes.map((item, index) => {
            return {
                keyForValue: 'paymentTypes',
                index,
                formattedValue: checkStringValueAndTranslate(item.toLowerCase(), 'document.paymentTypesList.', t),
            };
        });
        chips = [...chips, ...items];
    }

    if (filterParams.taxRates && filterParams.taxRates.length > 0) {
        const items: FilterChipProps<DocumentsListFilterParams>[] = filterParams.taxRates.map((item, index) => {
            return {
                keyForValue: 'taxRates',
                index,
                formattedValue: `${t('document.taxRate')}: ${checkStringValueAndTranslate(
                    item,
                    'document.taxRates.',
                    t,
                )}`,
            };
        });
        chips = [...chips, ...items];
    }

    if (filterParams.restored !== null) {
        chips.push({
            keyForValue: 'restored',
            formattedValue:
                filterParams.restored.toString() === 'true'
                    ? `${t('document.restored')}: ${t('app.yes')}`
                    : `${t('document.restored')}: ${t('app.no')}`,
            resetValue: null,
        });
    }

    if (filterParams.sumFrom || filterParams.sumTo) {
        chips.push({
            keyForValue: 'sumFrom',
            pairedKeyForValue: 'sumTo',
            formattedValue: makeChipTextForRangeFilter(
                t('document.receiptAmount'),
                filterParams.sumFrom,
                filterParams.sumTo,
            ),
        });
    }
    return chips;
};

const documentsListConfig: ListTableColumnConfig<DocumentsListTableData>[] = [
    {
        keyForValue: 'title',
        translatePath: 'app.name',
        isBold: true,
        align: 'left',
        maxWidth: 300,
        displayCustomCell: true,
    },
    {
        keyForValue: 'documentId',
        translatePath: 'document.fiscalId',
        isSearchable: true,
        maxWidth: 300,
        isWithTooltip: true,
        link: {
            routeId: RouteId.RegistryDocumentCard,
            routeParamName: RouteParam.DocumentId,
            routeParamKeyForValue: 'documentId',
        },
        isBold: true,
        align: 'left',
    },
    {
        keyForValue: 'documentIdHash',
        translatePath: 'app.link',
        displayCustomCell: true,
        align: 'center',
    },
    {
        keyForValue: 'rrn',
        translatePath: 'document.retrievalReferenceNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
    },
    {
        keyForValue: 'parentDocumentId',
        translatePath: 'document.parentFiscalId',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'cashBoxTaxNumber',
        translatePath: 'cashBox.cashboxTaxNumber',
        isSearchable: true,
        maxWidth: 300,
        isWithTooltip: true,
        link: {
            routeId: RouteId.RegistryCashboxCard,
            routeParamName: RouteParam.CashboxTaxNumber,
            routeParamKeyForValue: 'cashBoxTaxNumber',
        },
        isBold: true,
        align: 'left',
    },
    {
        keyForValue: 'storeTaxNumber',
        translatePath: 'store.storeTaxNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'companyTaxNumber',
        translatePath: 'company.companyTaxNumber',
        isSearchable: true,
        isHidden: true, //used for search filter
        align: 'left',
    },
    {
        keyForValue: 'createdAt',
        translatePath: 'app.dateTime',
        align: 'left',
    },
    {
        keyForValue: 'sum',
        translatePath: 'document.sum',
        align: 'right',
    },
];

export const docNumbersSearchFilterConfig: ListTableColumnConfig<DocumentsListTableData>[] = [
    {
        keyForValue: 'docNumber',
        translatePath: 'document.docNumber',
        isSearchable: true,
    },
    {
        keyForValue: 'globalDocumentNumber',
        translatePath: 'document.globalDocNumber',
        isSearchable: true,
    },
    {
        keyForValue: 'documentNumberInShift',
        translatePath: 'document.positionInShift',
        isSearchable: true,
    },
];

export const getDocumentsListConfigByPermits = (ability: AppAbility) => {
    const keysToRemove: (keyof DocumentsListTableData)[] = [];

    if (ability.can('access', 'DOCUMENT_ID')) {
        //for inspector
        keysToRemove.push('documentIdHash');
    }

    if (ability.cannot('viewScreen', 'DOCUMENT_CARD_PICT_SCREEN')) {
        keysToRemove.push('documentIdHash');
    }

    if (ability.cannot('access', 'DOCUMENT_ID')) {
        keysToRemove.push('documentId');
        keysToRemove.push('parentDocumentId');
    }

    if (ability.cannot('access', 'RELATED_COMPANY_DATA')) {
        keysToRemove.push('companyTaxNumber');
    }

    if (ability.cannot('access', 'RRN_FILTER')) {
        keysToRemove.push('rrn');
    }

    if (keysToRemove.length > 0)
        return filterConfigToOmitKeys<DocumentsListTableData>(documentsListConfig, keysToRemove);
    else return documentsListConfig;
};

export const checkIsTimeRangeFilterHidden = (filterParams: DocumentsListFilterParams): boolean => {
    if (
        ((filterParams.search_type === 'documentId' ||
            filterParams.search_type === 'parentDocumentId' ||
            filterParams.search_type === 'rrn') &&
            filterParams.search_text?.length > 0) ||
        (filterParams.search_type === 'cashBoxTaxNumber' &&
            filterParams.search_text?.length > 0 &&
            filterParams.docNumbersSearchText?.length > 0)
    )
        return true;
    return false;
};

export const checkIsDocNumbersFilterShown = (filterParams: Partial<DocumentsListFilterParams>): boolean => {
    return filterParams.search_type === 'cashBoxTaxNumber' && filterParams.search_text?.length > 0;
};

export const createGeneralGroupData = (
    document: DocumentsListTableData,
    t: WithTranslation['t'],
    ability: AppAbility,
): NameValueDataItem[] => {
    let data: NameValueDataItem[] = [];

    const isDoumentIdAllowedToSee = ability.can('access', 'DOCUMENT_ID');

    if (isDoumentIdAllowedToSee) {
        data.push({
            name: `${t('document.fiscalId')}:`,
            value: document.documentId,
            valueTooltip: true,
            link: {
                routeId: RouteId.RegistryDocumentCard,
                routeParamName: RouteParam.DocumentId,
                routeParamValue: document.documentId,
            },
        });
    }
    data = [
        ...data,
        {
            name: `${t('company.companyTaxNumber')}:`,
            value: document.companyTaxNumber,
            link:
                document.companyTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryCompanyCard,
                          routeParamName: RouteParam.CompanyTaxNumber,
                          routeParamValue: document.companyTaxNumber,
                      }
                    : null,
        },
        {
            name: `${t('company.companyName')}:`,
            value: document.companyName,
            valueTooltip: true,
        },
        {
            name: `${t('store.storeTaxNumber')}:`,
            value: document.storeTaxNumber,
            link:
                document.storeTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryStoreCard,
                          routeParamName: RouteParam.StoreTaxNumber,
                          routeParamValue: document.storeTaxNumber,
                      }
                    : null,
        },
        {
            name: `${t('cashBox.cashboxTaxNumber')}:`,
            value: document.cashBoxTaxNumber,
            link:
                document.cashBoxTaxNumber !== DATA_UNAVAILABLE
                    ? {
                          routeId: RouteId.RegistryCashboxCard,
                          routeParamName: RouteParam.CashboxTaxNumber,
                          routeParamValue: document.cashBoxTaxNumber,
                      }
                    : null,
        },
    ];
    if (isDoumentIdAllowedToSee && document.parentDocumentId)
        data.push({
            name: `${t('document.parentFiscalId')}:`,
            value: document.parentDocumentId,
            valueTooltip: true,
            link: {
                routeId: RouteId.RegistryDocumentCard,
                routeParamName: RouteParam.DocumentId,
                routeParamValue: document.parentDocumentId,
            },
        });

    return data;
};

export const createDetailsGroupData = (
    document: DocumentsListTableData,
    t: WithTranslation['t'],
): NameValueDataItem[] => {
    let data: NameValueDataItem[];
    switch (document.docTypeId) {
        case DocType.Sale:
        case DocType.MoneyBack:
        case DocType.RollBack:
        case DocType.Prepayment:
        case DocType.CreditPay:
            data = [
                {
                    name: `${t('document.taxRate')}:`,
                    value: document.taxRates,
                    valueColor: theme.palette.primary.main,
                },
                {
                    name: `${t('document.paymentType')}:`,
                    value: document.paymentTypes.toString(),
                    customValueContent: Array.isArray(document.paymentTypes)
                        ? () => paymentTypesAsIcons(document.paymentTypes as string[], t)
                        : null,
                },
                {
                    name: `${t('document.sum')}:`,
                    value: document.sum,
                    valueColor: theme.palette.error.main,
                },
                {
                    name: `${t('document.taxSum')}:`,
                    value: document.taxSum,
                    valueColor: theme.palette.error.main,
                },
            ];
            break;
        case DocType.Correction:
            data = [
                {
                    name: `${t('domain.period')}:`,
                    value: document.period,
                },
                {
                    name: `${t('document.taxRate')}:`,
                    value: document.taxRates,
                    valueColor: theme.palette.primary.main,
                },
                {
                    name: `${t('document.paymentType')}:`,
                    value: document.paymentTypes.toString(),
                    customValueContent: Array.isArray(document.paymentTypes)
                        ? () => paymentTypesAsIcons(document.paymentTypes as string[], t)
                        : null,
                },
                {
                    name: `${t('document.sum')}:`,
                    value: document.sum,
                    valueColor: theme.palette.error.main,
                },
                {
                    name: `${t('document.taxSum')}:`,
                    value: document.taxSum,
                    valueColor: theme.palette.error.main,
                },
            ];
            break;
        case DocType.OpenShift:
            data = [];
            break;
        case DocType.ZReport:
            data = [
                {
                    name: `${t('domain.period')}:`,
                    value: document.period,
                },
                {
                    name: `${t('document.zReport.numberOfDocumentsPerShift')}:`,
                    value: document.numberOfDocs,
                    valueColor: theme.palette.primary.main,
                },
                {
                    name: `${t('document.sum')}:`,
                    value: document.sum,
                    valueColor: theme.palette.error.main,
                },
                {
                    name: `${t('document.taxSum')}:`,
                    value: document.taxSum,
                    valueColor: theme.palette.error.main,
                },
            ];
            break;
        default:
            data = [];
            break;
    }
    data.unshift({
        name: `${t('app.dateTime')}:`,
        value: document.createdAt,
    });

    return data;
};
